import React, {useState, useEffect} from 'react';
import {toast} from 'react-toastify';
import {migratePersonaTools, getPersonasUsingServer} from './personaToolMigration';
import './SettingsModal.css';

// Sensitive header keys that should be hidden by default
const SENSITIVE_HEADERS = [
    'authorization',
    'x-api-key',
    'x-litellm-api-key',
    'api-key',
    'bearer',
    'token',
    'password',
    'secret'
];

// Helper to check if header key is sensitive
const isSensitiveHeader = (headerKey) => {
    const lowerKey = headerKey.toLowerCase();
    return SENSITIVE_HEADERS.some(sensitive => lowerKey.includes(sensitive));
};

/**
 * Settings Modal Component
 * Manages personas and MCP server configuration
 * ✅ Extracted from App.jsx for better code organization
 */
function SettingsModal({
                           mcpServers,
                           sessionId,
                           llmConfig,
                           setLlmConfig,
                           personas: currentPersonas,
                           onPersonasChange,
                           onClose,
                           onSave,
                           onToolsDiscovered,
                           availableTools
                       }) {
    const [activeTab, setActiveTab] = useState('personas');
    const [servers, setServers] = useState(mcpServers);
    const [newServer, setNewServer] = useState({
        name: '',
        url: '',
        headers: [
            {key: 'x-litellm-api-key', value: 'Bearer '},
            {key: 'Authorization', value: ''}
        ]
    });
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [editingPersona, setEditingPersona] = useState(null);
    const [showPersonaModal, setShowPersonaModal] = useState(false);
    const [showMcpModal, setShowMcpModal] = useState(false);
    const [editingServer, setEditingServer] = useState(null);
    const [headerVisibility, setHeaderVisibility] = useState({}); // Track show/hide state for each header

    const addHeader = () => {
        setNewServer({
            ...newServer,
            headers: [...newServer.headers, {key: '', value: ''}]
        });
    };

    const updateHeader = (index, field, value) => {
        const updatedHeaders = [...newServer.headers];
        updatedHeaders[index][field] = value;
        setNewServer({...newServer, headers: updatedHeaders});
    };

    const removeHeader = (index) => {
        setNewServer({
            ...newServer,
            headers: newServer.headers.filter((_, i) => i !== index)
        });
    };

    const testConnection = async () => {
        if (!newServer.name || !newServer.url) {
            setTestResult({success: false, error: 'Name and URL are required'});
            toast.error('Name and URL are required', {
                toastId: 'mcp-validation-error',
                autoClose: 5000
            });
            return;
        }

        setTesting(true);
        setTestResult(null);

        try {
            const serverToTest = {
                name: newServer.name,
                url: newServer.url,
                headers: newServer.headers
            };

            const result = await discoverMCPTools(serverToTest);

            if (result.error) {
                throw new Error(result.error);
            }

            const toolNames = result.tools.map(t => t.name);

            setTestResult({
                success: true,
                message: `✓ Connected! Found ${toolNames.length} tools`,
                tools: toolNames.length,
                tool_names: toolNames
            });

            toast.success(`${newServer.name} connected successfully! Found ${toolNames.length} tools`, {
                toastId: `mcp-test-${newServer.name}`,
                autoClose: 3000
            });

            const headersObj = {};
            newServer.headers.forEach(h => {
                if (h.key && h.value) {
                    headersObj[h.key] = h.value;
                }
            });

            let updatedServers;

            if (editingServer) {
                const oldServerName = editingServer.name;
                const newServerName = newServer.name;

                const updatedServerData = {
                    ...editingServer,
                    name: newServer.name,
                    url: newServer.url,
                    headers: headersObj,
                    tools: toolNames,
                    toolCount: toolNames.length,
                    status: 'connected'
                };
                updatedServers = servers.map(s => s.id === editingServer.id ? updatedServerData : s);

                // If server name changed, migrate persona tools
                if (oldServerName !== newServerName) {
                    const affectedPersonaNames = getPersonasUsingServer(currentPersonas, oldServerName);

                    if (affectedPersonaNames.length > 0) {
                        const {personas: migratedPersonas, affectedCount} = migratePersonaTools(
                            currentPersonas,
                            oldServerName,
                            newServerName
                        );

                        if (affectedCount > 0) {
                            onPersonasChange(migratedPersonas);
                            toast.success(
                                `Migrated ${affectedCount} tool${affectedCount > 1 ? 's' : ''} in ${affectedPersonaNames.length} persona${affectedPersonaNames.length > 1 ? 's' : ''} to use "${newServerName}"`,
                                {autoClose: 5000}
                            );
                        }
                    }
                }
            } else {
                const newServerData = {
                    name: newServer.name,
                    url: newServer.url,
                    headers: headersObj,
                    id: Date.now(),
                    tools: toolNames,
                    toolCount: toolNames.length,
                    status: 'connected'
                };
                updatedServers = [...servers, newServerData];
            }

            setServers(updatedServers);

            // Notify parent to update availableTools immediately
            if (onToolsDiscovered) {
                const allDiscoveredTools = updatedServers
                    .filter(s => s.status === 'connected' && s.tools)
                    .flatMap(server =>
                        server.tools.map(toolName => ({
                            name: `mcp__${server.name}__${toolName}`,
                            description: toolName,
                            type: 'http_mcp',
                            server: server.name,
                            enabled_by_default: true
                        }))
                    );
                onToolsDiscovered(allDiscoveredTools);
            }

            setNewServer({
                name: '',
                url: '',
                headers: [
                    {key: 'x-litellm-api-key', value: 'Bearer '},
                    {key: 'Authorization', value: ''}
                ]
            });
            setEditingServer(null);
            setShowMcpModal(false);
        } catch (error) {
            setTestResult({success: false, error: error.message});
            toast.error(`Failed to connect to ${newServer.name}: ${error.message}`, {
                toastId: `mcp-test-error-${newServer.name}`,
                autoClose: 8000
            });
        } finally {
            setTesting(false);
        }
    };

    const removeServer = (id) => {
        const updatedServers = servers.filter(s => s.id !== id);
        setServers(updatedServers);
    };

    useEffect(() => {
        onSave(servers);
    }, [servers]);

    const handleClose = () => {
        onSave(servers);
        localStorage.setItem('llm_config', JSON.stringify(llmConfig));
        onClose();
    };

    const handleSavePersona = () => {
        if (!editingPersona.name || !editingPersona.description || !editingPersona.system_prompt) {
            alert('Please fill in all fields');
            return;
        }

        if (!editingPersona.id) {
            editingPersona.id = editingPersona.name.toLowerCase().replace(/\s+/g, '-');
        }

        const updatedPersonas = currentPersonas.find(p => p.id === editingPersona.id)
            ? currentPersonas.map(p => p.id === editingPersona.id ? editingPersona : p)
            : [...currentPersonas, editingPersona];

        onPersonasChange(updatedPersonas);
        setShowPersonaModal(false);
        setEditingPersona(null);
    };

    return (
        <div className="drawer-overlay" onClick={handleClose}>
            <div className="drawer-content" onClick={(e) => e.stopPropagation()}>
                <div className="drawer-header">
                    <h2>Settings</h2>
                    <button className="drawer-close" onClick={handleClose}>×</button>
                </div>

                <div className="drawer-tabs">
                    <button
                        className={`tab ${activeTab === 'personas' ? 'active' : ''}`}
                        onClick={() => setActiveTab('personas')}
                    >
                        Personas ({currentPersonas.length})
                    </button>
                    <button
                        className={`tab ${activeTab === 'mcp-servers' ? 'active' : ''}`}
                        onClick={() => setActiveTab('mcp-servers')}
                    >
                        MCP Servers ({servers.length})
                    </button>
                </div>

                <div className="drawer-body">
                    {activeTab === 'personas' && (
                        <div className="personas-list">
                            <div style={{
                                marginBottom: '16px',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                alignItems: 'center'
                            }}>
                                <button
                                    onClick={() => {
                                        setEditingPersona({
                                            id: Date.now(),
                                            name: '',
                                            description: '',
                                            system_prompt: '',
                                            tools: []
                                        });
                                        setShowPersonaModal(true);
                                    }}
                                    className="add-btn"
                                    style={{padding: '8px 16px', fontSize: '14px'}}
                                >
                                    Create Persona
                                </button>
                            </div>

                            {currentPersonas.map((persona) => (
                                <div key={persona.id} className="server-card">
                                    <div className="server-card-header">
                                        <div>
                                            <h4>🤖 {persona.name}</h4>
                                            <p style={{margin: '4px 0 0 0', fontSize: '13px', color: '#666'}}>
                                                {persona.description}
                                            </p>
                                        </div>
                                        <div style={{display: 'flex', gap: '8px'}}>
                                            <button
                                                onClick={() => {
                                                    setEditingPersona(persona);
                                                    setShowPersonaModal(true);
                                                }}
                                                className="link-btn"
                                                style={{padding: '4px 12px', fontSize: '13px'}}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => {
                                                    if (confirm(`Delete persona "${persona.name}"?`)) {
                                                        onPersonasChange(currentPersonas.filter(p => p.id !== persona.id));
                                                    }
                                                }}
                                                className="remove-btn"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                    <div className="server-card-body">
                                        <div className="server-detail">
                                            <span className="label">Tools:</span>
                                            <span
                                                className="value">{persona.tools.length} tool{persona.tools.length !== 1 ? 's' : ''}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {currentPersonas.length === 0 && (
                                <div className="empty-state">
                                    <p>No personas configured yet</p>
                                    <button
                                        onClick={() => {
                                            setEditingPersona({
                                                id: Date.now(),
                                                name: '',
                                                description: '',
                                                system_prompt: '',
                                                tools: []
                                            });
                                            setShowPersonaModal(true);
                                        }}
                                        className="link-btn"
                                    >
                                        Create your first persona →
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'mcp-servers' && (
                        <div className="server-list">
                            <div style={{
                                marginBottom: '16px',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                alignItems: 'center'
                            }}>
                                <button
                                    onClick={() => {
                                        setNewServer({
                                            name: '',
                                            url: '',
                                            headers: [
                                                {key: 'x-litellm-api-key', value: 'Bearer '},
                                                {key: 'Authorization', value: ''}
                                            ]
                                        });
                                        setTestResult(null);
                                        setShowMcpModal(true);
                                    }}
                                    className="add-btn"
                                    style={{padding: '8px 16px', fontSize: '14px'}}
                                >
                                    Add MCP Server
                                </button>
                            </div>

                            {servers.map((server) => (
                                <div key={server.id}
                                     className={`server-card ${server.status === 'failed' ? 'failed' : ''}`}>
                                    <div className="server-card-header">
                                        <div>
                                            <h4>
                                                {server.name}
                                                {server.status === 'connected' &&
                                                    <span className="status-badge connected">✓ Success</span>}
                                                {server.status === 'failed' &&
                                                    <span className="status-badge failed">✗ Failed</span>}
                                                {server.status === 'connecting' &&
                                                    <span className="status-badge connecting">⋯ Connecting</span>}
                                            </h4>
                                            {server.toolCount > 0 &&
                                                <span className="tool-count">{server.toolCount} tools</span>}
                                        </div>
                                        <div style={{display: 'flex', gap: '8px'}}>
                                            <button
                                                onClick={() => {
                                                    let headersArray = [];

                                                    if (server.headers) {
                                                        if (Array.isArray(server.headers)) {
                                                            headersArray = server.headers;
                                                        } else if (typeof server.headers === 'object') {
                                                            headersArray = Object.entries(server.headers).map(([key, value]) => ({
                                                                key,
                                                                value
                                                            }));
                                                        }
                                                    }

                                                    const headers = headersArray.length > 0 ? headersArray : [
                                                        {key: 'x-litellm-api-key', value: 'Bearer '},
                                                        {key: 'Authorization', value: ''}
                                                    ];

                                                    setEditingServer(server);
                                                    setNewServer({
                                                        name: server.name,
                                                        url: server.url,
                                                        headers: headers
                                                    });
                                                    setTestResult(null);
                                                    setShowMcpModal(true);
                                                }}
                                                className="link-btn"
                                                style={{padding: '4px 12px', fontSize: '13px'}}
                                            >
                                                Edit
                                            </button>
                                            <button onClick={() => removeServer(server.id)}
                                                    className="remove-btn">Remove
                                            </button>
                                        </div>
                                    </div>
                                    <div className="server-card-body">
                                        <div className="server-detail">
                                            <span className="label">URL:</span>
                                            <span className="value">{server.url}</span>
                                        </div>
                                        {server.error && server.status === 'failed' && (
                                            <div className="server-detail error">
                                                <span className="label">Error:</span>
                                                <span className="value error-text">{server.error}</span>
                                            </div>
                                        )}
                                        {server.headers && Object.keys(server.headers).length > 0 && (
                                            <div className="server-detail">
                                                <span className="label">Headers:</span>
                                                <span
                                                    className="value">{Object.keys(server.headers).length} configured</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}

                            {servers.length === 0 && (
                                <div className="empty-state">
                                    <p>No MCP servers connected yet</p>
                                    <button
                                        onClick={() => {
                                            setNewServer({
                                                name: '',
                                                url: '',
                                                headers: [
                                                    {key: 'x-litellm-api-key', value: 'Bearer '},
                                                    {key: 'Authorization', value: ''}
                                                ]
                                            });
                                            setTestResult(null);
                                            setShowMcpModal(true);
                                        }}
                                        className="link-btn"
                                    >
                                        Add your first MCP server →
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Persona Editor Modal */}
                {showPersonaModal && editingPersona && (
                    <div className="modal-overlay" onClick={() => setShowPersonaModal(false)} style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10000
                    }}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{
                            background: 'white',
                            borderRadius: '12px',
                            padding: '24px',
                            maxWidth: '600px',
                            width: '90%',
                            maxHeight: '90vh',
                            overflow: 'auto'
                        }}>
                            <h3 style={{marginTop: 0}}>{editingPersona.id && currentPersonas.find(p => p.id === editingPersona.id) ? 'Edit Persona' : 'Create Persona'}</h3>

                            <div style={{marginBottom: '16px'}}>
                                <label style={{display: 'block', marginBottom: '4px', fontWeight: 600}}>Display Name
                                    *</label>
                                <input
                                    type="text"
                                    placeholder="e.g., Analytics Expert"
                                    value={editingPersona.name}
                                    onChange={(e) => setEditingPersona({
                                        ...editingPersona,
                                        name: e.target.value,
                                        id: e.target.value.toLowerCase().replace(/\s+/g, '-')
                                    })}
                                    style={{
                                        width: '100%',
                                        padding: '8px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px'
                                    }}
                                />
                            </div>

                            <div style={{marginBottom: '16px'}}>
                                <label style={{display: 'block', marginBottom: '4px', fontWeight: 600}}>Description
                                    (When to use this persona) *</label>
                                <textarea
                                    placeholder="Expert in SQL queries, Athena database operations, and data analysis"
                                    value={editingPersona.description}
                                    onChange={(e) => setEditingPersona({
                                        ...editingPersona,
                                        description: e.target.value
                                    })}
                                    rows={2}
                                    style={{
                                        width: '100%',
                                        padding: '8px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontFamily: 'inherit'
                                    }}
                                />
                            </div>

                            <div style={{marginBottom: '16px'}}>
                                <label style={{display: 'block', marginBottom: '4px', fontWeight: 600}}>System Prompt
                                    *</label>
                                <textarea
                                    placeholder="You are a data analyst specialized in router telemetry analysis..."
                                    value={editingPersona.system_prompt}
                                    onChange={(e) => setEditingPersona({
                                        ...editingPersona,
                                        system_prompt: e.target.value
                                    })}
                                    rows={8}
                                    style={{
                                        width: '100%',
                                        padding: '8px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontFamily: 'monospace',
                                        fontSize: '13px'
                                    }}
                                />
                            </div>

                            <div style={{marginBottom: '16px'}}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '8px'
                                }}>
                                    <label style={{fontWeight: 600}}>Available Tools</label>
                                    <div style={{display: 'flex', gap: '8px'}}>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const allToolNames = availableTools.map(t => t.name);
                                                setEditingPersona({...editingPersona, tools: allToolNames});
                                            }}
                                            style={{
                                                padding: '4px 12px',
                                                fontSize: '12px',
                                                background: '#4A90E2',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontWeight: 500
                                            }}
                                        >
                                            Select All
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setEditingPersona({...editingPersona, tools: []});
                                            }}
                                            style={{
                                                padding: '4px 12px',
                                                fontSize: '12px',
                                                background: '#999',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontWeight: 500
                                            }}
                                        >
                                            Clear All
                                        </button>
                                    </div>
                                </div>
                                <div style={{
                                    maxHeight: '150px',
                                    overflow: 'auto',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    padding: '8px'
                                }}>
                                    {availableTools.map((tool) => {
                                        const isMandatory = MANDATORY_TOOLS.includes(tool.name);
                                        return (
                                            <label
                                                key={tool.name}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    marginBottom: '4px',
                                                    cursor: isMandatory ? 'not-allowed' : 'pointer',
                                                    opacity: isMandatory ? 0.7 : 1
                                                }}
                                                title={isMandatory ? 'This tool is mandatory and cannot be disabled' : ''}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={editingPersona.tools.includes(tool.name)}
                                                    disabled={isMandatory}
                                                    onChange={(e) => {
                                                        if (!isMandatory) {
                                                            if (e.target.checked) {
                                                                setEditingPersona({
                                                                    ...editingPersona,
                                                                    tools: [...editingPersona.tools, tool.name]
                                                                });
                                                            } else {
                                                                setEditingPersona({
                                                                    ...editingPersona,
                                                                    tools: editingPersona.tools.filter(t => t !== tool.name)
                                                                });
                                                            }
                                                        }
                                                    }}
                                                    style={{
                                                        marginRight: '8px',
                                                        cursor: isMandatory ? 'not-allowed' : 'pointer'
                                                    }}
                                                />
                                                <span>{tool.name}</span>
                                                {isMandatory && (
                                                    <span style={{
                                                        marginLeft: '8px',
                                                        fontSize: '12px',
                                                        padding: '2px 6px',
                                                        background: '#f0f0f0',
                                                        border: '1px solid #ccc',
                                                        borderRadius: '3px',
                                                        color: '#666',
                                                        fontWeight: '500'
                                                    }}>
                            🔒 Required
                          </span>
                                                )}
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>

                            <div style={{display: 'flex', gap: '8px', justifyContent: 'flex-end'}}>
                                <button onClick={() => setShowPersonaModal(false)} className="modal-btn cancel">
                                    Cancel
                                </button>
                                <button onClick={handleSavePersona} className="modal-btn confirm">
                                    Save Persona
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* MCP Server Modal */}
                {showMcpModal && (
                    <div className="modal-overlay" onClick={() => {
                        setShowMcpModal(false);
                        setEditingServer(null);
                    }} style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10000
                    }}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{
                            background: 'white',
                            borderRadius: '12px',
                            padding: '24px',
                            maxWidth: '600px',
                            width: '90%',
                            maxHeight: '90vh',
                            overflow: 'auto'
                        }}>
                            <h3 style={{marginTop: 0}}>{editingServer ? 'Edit MCP Server' : 'Add MCP Server'}</h3>

                            <div style={{marginBottom: '16px'}}>
                                <label style={{display: 'block', marginBottom: '4px', fontWeight: 600}}>Server Name
                                    *</label>
                                <input
                                    type="text"
                                    placeholder="e.g., Jira Production"
                                    value={newServer.name}
                                    onChange={(e) => setNewServer({...newServer, name: e.target.value})}
                                    style={{
                                        width: '100%',
                                        padding: '8px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px'
                                    }}
                                />
                            </div>

                            <div style={{marginBottom: '16px'}}>
                                <label style={{display: 'block', marginBottom: '4px', fontWeight: 600}}>Server URL
                                    *</label>
                                <input
                                    type="text"
                                    placeholder="e.g., http://localhost:8080/jira"
                                    value={newServer.url}
                                    onChange={(e) => setNewServer({...newServer, url: e.target.value})}
                                    style={{
                                        width: '100%',
                                        padding: '8px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px'
                                    }}
                                />
                            </div>

                            <div style={{marginBottom: '16px'}}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '8px'
                                }}>
                                    <label style={{fontWeight: 600}}>HTTP Headers</label>
                                    <button
                                        onClick={addHeader}
                                        style={{
                                            padding: '4px 12px',
                                            fontSize: '13px',
                                            background: 'var(--verizon-vibrant-red)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        + Add Header
                                    </button>
                                </div>
                                {newServer.headers.map((header, index) => {
                                    const isSensitive = isSensitiveHeader(header.key);
                                    const isVisible = headerVisibility[index] || false;

                                    return (
                                        <div key={index} style={{
                                            display: 'flex',
                                            gap: '8px',
                                            marginBottom: '8px',
                                            alignItems: 'center'
                                        }}>
                                            <input
                                                type="text"
                                                placeholder="Header Key (e.g., Authorization)"
                                                value={header.key}
                                                onChange={(e) => updateHeader(index, 'key', e.target.value)}
                                                style={{
                                                    flex: 1,
                                                    padding: '8px',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '4px'
                                                }}
                                            />
                                            <div style={{flex: 1, display: 'flex', gap: '4px', alignItems: 'center'}}>
                                                <input
                                                    type={isSensitive && !isVisible ? "password" : "text"}
                                                    placeholder="Header Value"
                                                    value={header.value}
                                                    onChange={(e) => updateHeader(index, 'value', e.target.value)}
                                                    style={{
                                                        flex: 1,
                                                        padding: '8px',
                                                        border: '1px solid #ddd',
                                                        borderRadius: '4px'
                                                    }}
                                                />
                                                {isSensitive && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setHeaderVisibility({
                                                            ...headerVisibility,
                                                            [index]: !isVisible
                                                        })}
                                                        style={{
                                                            padding: '8px 12px',
                                                            background: '#f0f0f0',
                                                            border: '1px solid #ddd',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            fontSize: '16px',
                                                            lineHeight: '1',
                                                            minWidth: '40px'
                                                        }}
                                                        title={isVisible ? 'Hide value' : 'Show value'}
                                                    >
                                                        {isVisible ? '🔒' : '👁️'}
                                                    </button>
                                                )}
                                            </div>
                                            {newServer.headers.length > 1 && (
                                                <button
                                                    onClick={() => removeHeader(index)}
                                                    style={{
                                                        padding: '8px 12px',
                                                        background: '#f44336',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        fontSize: '16px',
                                                        lineHeight: '1'
                                                    }}
                                                >
                                                    ×
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {testResult && (
                                <div style={{
                                    padding: '12px',
                                    borderRadius: '6px',
                                    marginBottom: '16px',
                                    background: testResult.success ? '#d4edda' : '#f8d7da',
                                    color: testResult.success ? '#155724' : '#721c24',
                                    border: `1px solid ${testResult.success ? '#c3e6cb' : '#f5c6cb'}`
                                }}>
                                    {testResult.success ? (
                                        <>✓ Connected! Found {testResult.tools} tools</>
                                    ) : (
                                        <>✗ Connection failed: {testResult.error}</>
                                    )}
                                </div>
                            )}

                            <div style={{display: 'flex', gap: '8px', justifyContent: 'flex-end'}}>
                                <button
                                    onClick={() => {
                                        setShowMcpModal(false);
                                        setTestResult(null);
                                        setEditingServer(null);
                                    }}
                                    className="modal-btn cancel"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={testConnection}
                                    className="modal-btn confirm"
                                    disabled={testing}
                                >
                                    {testing ? 'Testing Connection...' : (editingServer ? 'Test & Update Server' : 'Test & Add Server')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default React.memo(SettingsModal);
